import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelCampaignRegistrationStatuses } from 'main/hotelStatuses'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import downloadAsCsv from 'utils/downloadAsCsv'

import { getHealthCheckBillingData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { NoteBatchLoaderProvider } from '../Common/NoteBatchLoaderContext'
import styles from './Billing.module.scss'
import { ReactComponent as ExcelIcon } from './excel.svg'
import BillingRow from './Row'
import BillingTitleRow from './TitleRow'
import TotalRow from './TotalRow'

function formatPrice(number) {
  return parseFloat(number).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

const emailTypes = {
  SubsidiaryInvoiceForPayment: 'Payment',
  SubsidiaryInvoiceForReconciliation: 'Reconciliation',
  SubsidiaryInvoiceForPaymentLateMuch: 'Payment late'
}

const HealthCheckBilling = () => {
  const dispatch = useDispatch()

  const [results, setResults] = useState([])
  const [totalBarData, setTotalBarData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('billing')

  useEffect(() => {
    dispatch(getHealthCheckBillingData())
  }, [dispatch])

  const isLoading = useSelector(s => s.healthCheck.billings.isLoading)
  const hotelData = useSelector(s => s.healthCheck.billings.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.billings.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.billings.hotelChains)

  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(true)

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState

  // Calculate row status for Billing tab
  const calculateRowStatus = row => {
    const { balanceCurrent, balanceLate, paymentLastReceivedOn, isAutoPayEnabled } = row

    const thirtyDaysAgo = moment().subtract(30, 'days')
    const hasRecentPayment =
      paymentLastReceivedOn && moment(paymentLastReceivedOn).isAfter(thirtyDaysAgo)

    // ALERT conditions:
    // 1. High late balance (> $1000)
    if (balanceLate > 1000) {
      return 'alert'
    }

    // 2. High current balance (> $5000) without recent payment
    if (balanceCurrent > 5000 && !hasRecentPayment) {
      return 'alert'
    }

    // 3. Late balance > 0 and no recent payment
    if (balanceLate > 0 && !hasRecentPayment) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. Some late balance but not critical (< $1000)
    if (balanceLate > 0 && balanceLate <= 1000) {
      return 'warning'
    }

    // 2. High current balance but has recent payment
    if (balanceCurrent > 5000 && hasRecentPayment) {
      return 'warning'
    }

    // 3. Moderate balance ($1000-$5000) without recent payment
    if (balanceCurrent >= 1000 && balanceCurrent <= 5000 && !hasRecentPayment) {
      return 'warning'
    }

    // GREEN conditions:
    // Low balance and up to date payments
    if (balanceCurrent < 1000 && balanceLate === 0 && (hasRecentPayment || isAutoPayEnabled)) {
      return 'green'
    }

    // Default to warning if status is unclear
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const { balanceCurrent, balanceLate, paymentLastReceivedOn, isAutoPayEnabled } = row

    const thirtyDaysAgo = moment().subtract(30, 'days')
    const hasRecentPayment =
      paymentLastReceivedOn && moment(paymentLastReceivedOn).isAfter(thirtyDaysAgo)

    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (balanceLate > 1000) {
        reasons.push(`High late balance: $${balanceLate.toLocaleString()}`)
      }
      if (balanceCurrent > 5000 && !hasRecentPayment) {
        reasons.push(`High current balance: $${balanceCurrent.toLocaleString()} (no recent payment)`)
      }
      if (balanceLate > 0 && !hasRecentPayment) {
        reasons.push(`Late balance: $${balanceLate.toLocaleString()} (no payment in 30+ days)`)
      }
    } else if (status === 'warning') {
      if (balanceLate > 0 && balanceLate <= 1000) {
        reasons.push(`Late balance: $${balanceLate.toLocaleString()}`)
      }
      if (balanceCurrent > 5000 && hasRecentPayment) {
        reasons.push(`High current balance: $${balanceCurrent.toLocaleString()} (has recent payment)`)
      }
      if (balanceCurrent >= 1000 && balanceCurrent <= 5000 && !hasRecentPayment) {
        reasons.push(`Moderate balance: $${balanceCurrent.toLocaleString()} (no recent payment)`)
      }
    } else if (status === 'green') {
      reasons.push(`Balance: $${balanceCurrent.toLocaleString()}`)
      if (balanceLate === 0) {
        reasons.push('No late balance')
      }
      if (hasRecentPayment || isAutoPayEnabled) {
        reasons.push(isAutoPayEnabled ? 'AutoPay enabled' : 'Recent payment received')
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  function handleClickDownloadAsCsv() {
    const titleRow = [
      'Hotel ID',
      'Hotel name',
      'Is AutoPay enabled',
      'Balance',
      'Late balance',
      'Last payment amount',
      'Last payment date',
      'Last email type',
      'Last email date',
      'Notes'
    ]

    const resAsArrays = results.map(b => {
      return [
        b.displayId,
        `${b.brand}-${b.shortName}`,
        b.isAutoPayEnabled ? 'Yes' : 'No',
        formatPrice(b.balanceCurrent),
        formatPrice(b.balanceLate),
        formatPrice(b.paymentLastAmount),
        b.paymentLastReceivedOn
          ? moment(b.paymentLastReceivedOn).format('MM/DD/YYYY')
          : '-',
        b.emailLastContent ? emailTypes[b.emailLastContent] : '-',
        b.emailLastSentOn ? moment(b.emailLastSentOn).format('MM/DD/YYYY') : '-',
        '-' // Notes are loaded per-row, not available in results array
      ]
    })

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `health_check_billing.csv`

    downloadAsCsv(dataArray, fileName)
  }

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds,
        hideZeroBalance,
        hideInvalidPaymentDate
      } = filterState

      const filtered = hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })
        .filter(h => {
          if (hideZeroBalance) {
            return h.balanceCurrent > 0
          }
          return true
        })
        .filter(h => {
          if (hideInvalidPaymentDate) {
            // Check if paymentLastReceivedOn is valid
            return h.paymentLastReceivedOn && moment(h.paymentLastReceivedOn).isValid()
          }
          return true
        })

      // Apply status filter
      return filtered.filter(h => {
        const status = calculateRowStatus(h)
        if (status === 'alert') return showAlert
        if (status === 'warning') return showWarning
        if (status === 'green') return showGreen
        return true
      })
    }

    function getSortedResults(hotelData, sortBy, sortDecreasing) {
      const titleRowNames = [
        'displayId',
        'shortName',
        'isAutoPayEnabled',
        'balanceCurrent',
        'balanceLate',
        'paymentLastAmount',
        'paymentLastReceivedOn',
        'emailLastSentOn'
      ]

      const sortByProp = titleRowNames[sortBy]

      const sortedData = hotelData.sort((a, b) => {
        if (sortByProp === 'paymentLastReceivedOn' || sortByProp === 'emailLastSentOn') {
          return (
            (b[sortByProp] ? Date.parse(b[sortByProp]) : 0) -
            (a[sortByProp] ? Date.parse(a[sortByProp]) : 0)
          )
        }

        if (typeof a[sortByProp] === 'string') {
          if (a[sortByProp] === b[sortByProp]) {
            return 0
          }
          return a[sortByProp]?.toUpperCase() > b[sortByProp]?.toUpperCase()
        } else if (typeof a[sortByProp] === 'boolean') {
          return Number(b[sortByProp]) - Number(a[sortByProp])
        } else {
          return b[sortByProp] - a[sortByProp]
        }
      })

      return sortDecreasing ? sortedData : sortedData.reverse()
    }

    function getResults() {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        sortByIndex,
        sortDecreasing
      )

      setResults(results)

      const totalBarData = results.reduce(
        (prev, curr) => {
          return {
            balanceCurrent: prev.balanceCurrent + curr.balanceCurrent,
            balanceLate: prev.balanceLate + curr.balanceLate,
            paymentAmount: prev.paymentAmount + curr.paymentLastAmount,
            bookingsCharged:
              prev.totalBookingValueToCharge + curr.totalBookingValueToCharge,
            bookingsNotCharged:
              prev.totalBookingValueNotCharged + curr.totalBookingValueNotCharged
          }
        },
        {
          balanceLate: 0,
          balanceCurrent: 0,
          paymentAmount: 0
        }
      )

      setTotalBarData({ ...totalBarData, count: results.length })
    }

    getResults()
  }, [filterState, hotelData, sortByIndex, sortDecreasing, showAlert, showWarning, showGreen])

  // Prepare subsidiary IDs for batch loading
  const subsidiaryIdsForBatchLoading = useMemo(() => {
    return results.map(h => ({
      subsidiaryId: h.subsidiaryId,
      kind: 'healthCheck-billing'
    }))
  }, [results])

  return (
    <NoteBatchLoaderProvider
      subsidiaryIds={subsidiaryIdsForBatchLoading}
      kind="healthCheck-billing"
    >
      <div className={styles.wrapper}>
        <CustomHeader title="Hotel health check" width={1180} isFixed />
        <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
      </Tab.Parent>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showSubsidiaryStatus
        showCsmFilter
        showHideZeroBalanceToggle
        showHideInvalidPaymentDateToggle
      >
        <div className={styles.alertStatusControls}>
          <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
          <Toggle
            className={styles.toggle}
            checked={showAlert}
            handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
            label="Alert"
          />
          <Toggle
            className={styles.toggle}
            checked={showWarning}
            handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
            label="Warning"
          />
          <Toggle
            className={styles.toggle}
            checked={showGreen}
            handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
            label="Green"
          />
        </div>
        <SecondaryRegularButton
          className={styles.copyToExcelButton}
          onClick={() => handleClickDownloadAsCsv()}
        >
          <ExcelIcon /> Download as CSV
        </SecondaryRegularButton>
      </HealthCheckFilters>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={150} />
        ) : (
          <Table
            widths={[40, 240, 100, 100, 100, 110, 115, 125]}
            handleTitleCellClick={handleTitleCellClick}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
          >
            <BillingTitleRow />
            {results.map((h, i) => {
              const rowStatus = calculateRowStatus(h)
              const statusExplanation = getStatusExplanation(h)
              return (
                <BillingRow
                  {...h}
                  index={i + 1}
                  rowStatus={rowStatus}
                  statusExplanation={statusExplanation}
                />
              )
            })}
            <TotalRow {...totalBarData} />
          </Table>
        )}
      </div>
    </div>
    </NoteBatchLoaderProvider>
  )
}

export default HealthCheckBilling
